import { Component, EventEmitter, Input, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { SelectMultipleDropdownComponent } from './dropdown/selectDropdown.component';
import * as uuid from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { CollectionAreasDialogComponent } from '../../dialogs/collection-areas-dialog/collection-areas-dialog.component';
import { LanguageField } from '@bciti/types';

@Component({
    selector: 'app-form-select-multiple',
    templateUrl: './select-multiple.component.html',
    styleUrls: ['./select-multiple.component.scss'],
})
export class FormSelectMultipleComponent implements ControlValueAccessor {
    public onChange: (v?: any) => {};
    public onTouched: () => {};

    private _value: any[] = [];
    private _options: {
        id: number;
        name: string | LanguageField;
        value: any;
        isSelected: boolean;
    }[];
    private _selected = [];
    // Support both @Input selected and @Input value
    // TODO: Refactor implementations to only use @Input value (if needed)

    @Input() label: string;
    @Input() placeholder: string;
    @Input() id: string = uuid.v4();
    @Input() disabled: boolean = false;
    @Input() validation: boolean = true;
    @Input() disableMessages: boolean = false;
    @Input() loading: boolean = false;
    @Input() displayAddArea: boolean = false;

    @Output() selectChange = new EventEmitter<any[]>();
    @Output() newClicked = new EventEmitter();
    @Output() blur = new EventEmitter();

    @ViewChild(SelectMultipleDropdownComponent)
    public dropdown: SelectMultipleDropdownComponent;

    @Input()
    get options() {
        return this._options;
    }
    set options(data: any[]) {
        if (!data) {
            return;
        }
        this._options = [...data];
    }

    @Input()
    get selected() {
        return this._selected;
    }
    set selected(data) {
        this._selected = data;
        this.value = data;
    }

    @Input()
    get value(): any[] {
        return this._value;
    }

    set value(v: any[]) {
        this._value = v;
        this.selectChange.emit(v);
        if (this.onChange) {
            this.onChange(v);
            this.onTouched();
        }
    }

    get invalid() {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid() {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine() {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty() {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched() {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    get requiredError() {
        if (this.ngControl && this.ngControl.hasError('required')) {
            return true;
        }
        return false;
    }

    get currentLang(): string {
        return this.translate.currentLang;
    }

    constructor(
        @Self() @Optional() private ngControl: NgControl,
        private translate: TranslateService,
        private dialog: MatDialog,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    onBlur(event: Event): void {
        this.blur.emit(event);
        if (this.onTouched) {
            this.onTouched();
        }
    }

    select(option: any) {
        const area = this._options.find((opt) => opt.id === option.id);
        area.isSelected = !area.isSelected;
        this.value = this._options.filter((opt) => opt.isSelected);
        this._selected = this.options.filter((opt) => opt.isSelected);
    }

    public toggleDropdown(): void {
        this.dropdown.showing ? this.hideDropdown() : this.showDropdown();
    }

    public showDropdown() {
        if (!this.loading) {
            this.dropdown.show();
        }
    }

    public hideDropdown() {
        this.dropdown?.hide();
    }

    writeValue(v: any[]): void {
        this._value = v;
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    multipleSelectedTranslation(): string {
        if (this.translate.currentLang === 'fr') {
            return this.value.length > 1 ? 's' : '';
        }
        return '';
    }

    openAddAeraDialog() {
        const areaDialog = this.dialog.open(CollectionAreasDialogComponent, {
            autoFocus: false,
            panelClass: 'add-area-dialog',
        });
        areaDialog.componentInstance.isCreatingOnly = true;
    }
}
